import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IComplaint } from "../../exports/interfaces";
import useMobile from "../../hooks/useMobile";
import Select from "react-select";
import { customSelectStyles } from "../../exports/customSelectStyles";
import { Pencil } from "lucide-react";
import DocumentPopUp from "../DocumentPopUp";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useLoader } from "../../context/LoaderContext";
import { usePermission } from "../../hooks/usePermission";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { fetchComplaints } from "../../api/ComplaintApi";
import { useOption } from "../../context/OptionContext";
import { darkenColor } from "../../exports/functions";

const getStatusClass = (status: string) => {
  switch (status) {
    case "Urgent":
      return "pill-danger";
    case "Not Urgent":
      return "pill-primary";
    case "Regular":
      return "pill-info";
    default:
      return "bg-secondary";
  }
};

const ViewComplaints = ({
  complaint,
  setComplaint,
}: {
  complaint: IComplaint | null;
  setComplaint: Dispatch<SetStateAction<IComplaint | null>>;
}) => {
  const { id } = useParams();
  const { statusOptions } = useOption();
  const { setLoading } = useLoader();
  const { canEdit } = usePermission();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [showDoc, setShowDoc] = useState(false);

  const statusComplaints =
    statusOptions
      ?.find((item) => item.name === "Complaint Statuses")
      ?.statusOptions?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const statusStyle = (status: string) => {
      const cooperativeStatuses =
        statusOptions?.find((item) => item.name === "Complaint Statuses")
          ?.statusOptions || [];
  
      const matchedStatus = cooperativeStatuses.find(
        (item) => item.label === status,
      );
  
      return {
        backgroundColor: matchedStatus?.color || "#ccc",
        color: darkenColor(matchedStatus?.color || "#ccc", 40),
      };
    };

  useEffect(() => {
    setStatus(complaint?.status || "");
  }, [complaint?.status]);

  const handleUpdate = async () => {
    setLoading(true);

    try {
      const payload = new FormData();

      payload.append("status", status);

      const response = await axiosClient.put(
        `/complaints/${complaint?.id}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 200 || response.status === 201) {
        fetchComplaints()
          .then((complaintData) => {
            const foundComplaint = complaintData.find(
              (item: IComplaint) => item.id === Number(id),
            );
            setComplaint(foundComplaint || null);
          })
          .catch((error) => {
            dynamicFailed(`${error.message}`);
          });
        dynamicSuccess("Complaint updated successfully!");
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  return (
    <>
      <div className="dashboard-card p-3">
        {/* Header */}
        <div className="dashboard-card">
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-end gap-2 flex-wrap">
              <div>
                <h5 className="mb-1">CMP-2026-{complaint?.id}</h5>

                <small className="d-block text-muted">
                  {complaint?.complaintType}
                </small>
              </div>

              <div className="mt-2 d-flex align-items-end gap-2">
                <span
                  className={`badge rounded-pill px-3 py-2`}
                  style={statusStyle(complaint?.status || "")}
                >
                  {complaint?.status}
                </span>

                <span
                  className={`badge rounded-pill px-3 py-2 ${getStatusClass(
                    complaint?.complaintPriority || "",
                  )}`}
                >
                  {complaint?.complaintPriority}
                </span>
              </div>
            </div>

            {canEdit("complaints") && (
              <button
                className="btn btn-badge fs-small"
                onClick={() => navigate(`/complaints/edit/${complaint?.id}`)}
              >
                <i className={`${!isMobile ? "me-2" : ""}`}>
                  <Pencil size={15} />
                </i>
                {!isMobile && "Edit"}
              </button>
            )}
          </div>

          <hr />

          <h6 className="primary-color mb-3">Complaint Information</h6>

          <div className="row g-3">
            <Info
              label="Complaint Name"
              value={complaint?.complainantName || "-"}
            />

            <Info label="Mobile" value={complaint?.complainantContact || "-"} />

            <Info label="Email Address" value={complaint?.email || "-"} />

            <Info label="Received Date" value={complaint?.filedOn || "-"} />
          </div>

          <hr />

          <h6 className="primary-color mb-3">Complaint Details</h6>

          <div className="row g-3">
            <Info
              label="Complaint Type"
              value={complaint?.complaintType || "-"}
            />

            <Info
              label="Complaint Priority"
              value={complaint?.complaintPriority || "-"}
            />

            <Info
              label="Complaint Area"
              value={complaint?.complaintArea || "-"}
            />

            <Info
              label="Cooperative Name"
              value={complaint?.cooperativeName || "-"}
            />

            <Info
              label="Cooperative Registration"
              value={complaint?.cooperativeRegistrationNumber || "-"}
            />

            <div className="col-md-8 details-text">
              <small className="d-block">Complaint Detail</small>

              <p>{complaint?.complainDetails}</p>
            </div>

            <div className="col-md-4 text-end">
              <button
                className="btn btn-badge-border px-4"
                onClick={() => setShowDoc(true)}
              >
                View Document
              </button>
            </div>
          </div>
        </div>
        <div className="row mt-1 g-3">
          <div className="col-lg-8">
            <div className="row">
              <div className="col-12">
                <div className="dashboard-card">
                  <h6 className="primary-color mb-3">Add Activity Note</h6>

                  <textarea
                    className="form-control"
                    rows={4}
                    placeholder="Describe the action taken"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />

                  <div className="text-end mt-3">
                    <button className="btn btn-badge-border">
                      <i className="fa-solid fa-plus"></i> Add Activity Log
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-12 mt-3">
                <div className="dashboard-card p-3">
                  <h6 className="primary-color mb-3">Activity Log</h6>

                  <p className="text-muted mb-0">No Activity yet</p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="dashboard-card">
              <div className="dashboard-card p-3">
                <h6 className="primary-color mb-3">Update Status</h6>

                <div className="mb-3">
                  <label className="form-label">Complaint Status</label>

                  <div>
                    <span
                      className={`badge rounded-pill px-3 py-2`}
                       style={statusStyle(complaint?.status || "")}
                    >
                      {complaint?.status}
                    </span>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Change To</label>
                  <Select
                    styles={customSelectStyles}
                    options={statusComplaints}
                    value={statusComplaints.find((o) => o.value === status)}
                    onChange={(selected) => setStatus(selected?.value || "")}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <button
                  className="btn btn-badge-border w-100"
                  onClick={handleUpdate}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DocumentPopUp
        showDoc={showDoc}
        setShowDoc={setShowDoc}
        docs={complaint?.documentUrl}
      />
    </>
  );
};

export const Info = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="col-md-4 details-text">
    <small className="d-block">{label}</small>
    <p>{value}</p>
  </div>
);

export default ViewComplaints;
